import { defaultWidgetSizes } from "@/utils/widgets";
import {
  breakpointCols,
  type BreakpointLayouts,
  type LayoutItem,
  type WidgetFilterData,
} from "@/utils/DashboardLayouts";
import type { LayoutExportData } from "@/utils/layoutExportImport";
import type { TemplateWidgetSpec } from "./types";

// Mirrors TabContent's grid maths — a generated dashboard has to agree with
// the renderer about how much canvas actually exists.
const HEADER_HEIGHT = 40;
const TAB_BAR_HEIGHT = 28;
const MARGIN = 3;
const TARGET_ROWS = 10;
const MIN_ROW_HEIGHT = 60;
const MAX_ROW_HEIGHT = 100;

/** Rows that actually fit on this viewport, not a fixed assumption. */
export function visibleRowCount(): number {
  if (typeof window === "undefined") return TARGET_ROWS;
  const availableHeight = window.innerHeight - HEADER_HEIGHT - TAB_BAR_HEIGHT;
  // rowHeight clamps at 100px, so tall screens expose MORE than TARGET_ROWS
  // rows — that headroom is exactly what a generated layout should claim.
  const rowHeight = Math.max(
    MIN_ROW_HEIGHT,
    Math.min(
      Math.floor((availableHeight - TARGET_ROWS * MARGIN) / TARGET_ROWS),
      MAX_ROW_HEIGHT
    )
  );
  const rows = Math.floor((availableHeight + MARGIN) / (rowHeight + MARGIN));
  return Math.max(6, Math.min(rows, 20));
}

type WidgetSize = { w: number; h: number; minW: number };

function baseIdOf(id: string): string {
  const dashIdx = id.indexOf("-");
  return dashIdx === -1 ? id : id.substring(0, dashIdx);
}

/** The widget's preferred proportions — used as weights, not absolutes. */
function sizeFor(spec: TemplateWidgetSpec): WidgetSize {
  const registrySize = (
    defaultWidgetSizes as Record<string, Partial<WidgetSize> | undefined>
  )[baseIdOf(spec.id)];
  const base = registrySize ?? { w: 4, h: 4, minW: 2 };
  return {
    w: spec.size?.w ?? base.w ?? 4,
    h: spec.size?.h ?? base.h ?? 4,
    minW: base.minW ?? 2,
  };
}

/** Greedily group widgets into rows that fit the column count, in order. */
function intoRows(specs: TemplateWidgetSpec[], cols: number): TemplateWidgetSpec[][] {
  const rows: TemplateWidgetSpec[][] = [];
  let row: TemplateWidgetSpec[] = [];
  let used = 0;
  for (const spec of specs) {
    const preferred = Math.min(sizeFor(spec).w, cols);
    if (row.length > 0 && used + preferred > cols) {
      rows.push(row);
      row = [];
      used = 0;
    }
    row.push(spec);
    used += preferred;
  }
  if (row.length > 0) rows.push(row);
  return rows;
}

/**
 * Distribute `total` units across `weights`, proportionally, honouring each
 * item's minimum and guaranteeing the result sums to exactly `total`.
 */
function distribute(weights: number[], total: number, mins: number[]): number[] {
  const weightSum = weights.reduce((a, b) => a + b, 0) || weights.length;
  const raw = weights.map((w) => (w / weightSum) * total);
  const out = raw.map((value, i) => Math.max(mins[i], Math.floor(value)));

  // Hand the rounding remainder to whoever was shortchanged most.
  let remainder = total - out.reduce((a, b) => a + b, 0);
  while (remainder > 0) {
    let best = 0;
    let bestGap = -Infinity;
    for (let i = 0; i < out.length; i++) {
      const gap = raw[i] - out[i];
      if (gap > bestGap) {
        bestGap = gap;
        best = i;
      }
    }
    out[best] += 1;
    remainder -= 1;
  }
  // Over-allocated (mins exceeded the budget): trim the widest first.
  while (remainder < 0) {
    let best = 0;
    let bestValue = -Infinity;
    for (let i = 0; i < out.length; i++) {
      if (out[i] > mins[i] && out[i] > bestValue) {
        bestValue = out[i];
        best = i;
      }
    }
    if (bestValue === -Infinity) break; // everything is at its minimum
    out[best] -= 1;
    remainder += 1;
  }
  return out;
}

/**
 * Packs an ordered widget list into a layout that FILLS the canvas: every row
 * spans the full column count and the rows together claim the full visible
 * height. Widget registry sizes act as proportional weights rather than fixed
 * dimensions, so a generated dashboard looks deliberate on a 13" laptop and
 * on a 32" monitor instead of huddling in the top-left corner.
 */
export function packLayouts(
  specs: TemplateWidgetSpec[],
  targetRows: number = visibleRowCount()
): BreakpointLayouts {
  const layout: BreakpointLayouts = { lg: [], md: [], sm: [], xs: [], xxs: [] };

  for (const bp of ["lg", "md", "sm", "xs", "xxs"] as const) {
    const cols = breakpointCols[bp];
    const items: LayoutItem[] = [];

    // Narrow breakpoints stack one widget per row (mobile is a carousel).
    if (cols <= 2) {
      let y = 0;
      for (const spec of specs) {
        const h = sizeFor(spec).h;
        items.push({ i: spec.id, x: 0, y, w: cols, h });
        y += h;
      }
      layout[bp] = items;
      continue;
    }

    const rows = intoRows(specs, cols);

    // Height budget: rows share the visible canvas, weighted by the tallest
    // widget in each. Never squeeze a row below 3 units (a usable feed).
    const rowWeights = rows.map((row) => Math.max(...row.map((s) => sizeFor(s).h)));
    const minRowHeights = rows.map(() => 3);
    const budget = Math.max(targetRows, minRowHeights.reduce((a, b) => a + b, 0));
    const rowHeights = distribute(rowWeights, budget, minRowHeights);

    let y = 0;
    rows.forEach((row, rowIndex) => {
      const widths = distribute(
        row.map((s) => sizeFor(s).w),
        cols,
        row.map(() => 1)
      );
      let x = 0;
      row.forEach((spec, i) => {
        items.push({ i: spec.id, x, y, w: widths[i], h: rowHeights[rowIndex] });
        x += widths[i];
      });
      y += rowHeights[rowIndex];
    });

    layout[bp] = items;
  }

  return layout;
}

/**
 * Builds a full LayoutExportData (the same versioned format the export/import
 * pipeline validates) from a tab title and an ordered widget spec list.
 */
export function buildLayoutExportData(
  tabTitle: string,
  specs: TemplateWidgetSpec[]
): LayoutExportData {
  const widgetFilters: Record<string, WidgetFilterData> = {};
  for (const spec of specs) {
    const entry: WidgetFilterData = {};
    if (spec.filter && Object.keys(spec.filter).length > 0) {
      entry.filter = spec.filter;
    }
    if (spec.highlight && Object.keys(spec.highlight).length > 0) {
      entry.highlight = spec.highlight;
    }
    if (entry.filter || entry.highlight) widgetFilters[spec.id] = entry;
  }

  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    tabTitle,
    enabledWidgets: specs.map((s) => s.id),
    layout: packLayouts(specs),
    ...(Object.keys(widgetFilters).length > 0 && { widgetFilters }),
  };
}
